import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Globe2, Phone, Mail, MapPin, Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import { toast } from "sonner";
import { COMPANY } from "@/lib/data";
import { trackConversion } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      trackConversion("Subscribe", { content_category: "Newsletter" });
      toast.success("Thanks for subscribing!", { description: "You'll receive our best study-abroad tips." });
      setEmail("");
    } catch (err) {
      if (err?.response?.status === 409) toast.info("You're already subscribed.");
      else toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative bg-[#0B1220] text-white pt-20 pb-8 mt-24">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid md:grid-cols-12 gap-10 pb-14 border-b border-white/10">
          <div className="md:col-span-4">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#0F3D8C] to-[#2563EB] flex items-center justify-center shadow-lg">
                <Globe2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="font-heading font-semibold text-lg">Globe Vista</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-white/50">Overseas Education</div>
              </div>
            </Link>
            <p className="mt-5 text-sm text-white/60 leading-relaxed max-w-sm">
              Hyderabad's most trusted overseas education & language-training institute. Founded by <span className="text-white/90">{COMPANY.founder}</span> — {COMPANY.tagline.toLowerCase()}.
            </p>
            <div className="flex gap-3 mt-6">
              <a
                href={COMPANY.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="footer-instagram"
                className="inline-flex items-center gap-2 px-4 h-9 rounded-full bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white text-xs font-medium hover:opacity-90 transition-opacity"
              >
                <Instagram className="w-4 h-4" />
                @globevistaoverseas
              </a>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs tracking-[0.2em] uppercase text-white/40 mb-4">Company</div>
            <ul className="space-y-3 text-sm">
              <li><a href="#why" className="hover:text-[#D4AF37]">Why Us</a></li>
              <li><a href="#services" className="hover:text-[#D4AF37]">Services</a></li>
              <li><a href="#process" className="hover:text-[#D4AF37]">Process</a></li>
              <li><Link to="/blog" className="hover:text-[#D4AF37]">Blog</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs tracking-[0.2em] uppercase text-white/40 mb-4">Destinations</div>
            <ul className="space-y-3 text-sm">
              <li><a href="#destinations" className="hover:text-[#D4AF37]">Study in UK</a></li>
              <li><a href="#destinations" className="hover:text-[#D4AF37]">Study in USA</a></li>
              <li><a href="#destinations" className="hover:text-[#D4AF37]">Study in Canada</a></li>
              <li><a href="#destinations" className="hover:text-[#D4AF37]">Study in Australia</a></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-xs tracking-[0.2em] uppercase text-white/40 mb-4">Newsletter</div>
            <p className="text-sm text-white/60 mb-4">Get the latest visa updates, scholarships & test-prep tips.</p>
            <form onSubmit={subscribe} className="flex gap-2" data-testid="newsletter-form">
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="newsletter-email"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40 rounded-full"
              />
              <Button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit"
                className="rounded-full bg-[#D4AF37] hover:bg-[#B99328] text-[#0F172A] font-semibold px-6"
              >
                {loading ? "..." : "Subscribe"}
              </Button>
            </form>

            <div className="mt-6 space-y-2.5 text-sm">
              <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-2 text-white/70 hover:text-white">
                <Phone className="w-4 h-4 text-[#D4AF37]" /> {COMPANY.phone}
              </a>
              <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 text-white/70 hover:text-white break-all">
                <Mail className="w-4 h-4 text-[#D4AF37]" /> {COMPANY.email}
              </a>
              <div className="flex items-start gap-2 text-white/70">
                <MapPin className="w-4 h-4 text-[#D4AF37] shrink-0 mt-0.5" /> {COMPANY.address}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-8 text-xs text-white/40">
          <div>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</div>
          <div className="flex gap-6">
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Terms</a>
            <a href="#" className="hover:text-white">Refund Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
